import { useState } from "react";
import { useAtom } from "@dbeining/react-atom";
import { userAtom } from "../../atoms/userAtom";
import { showToastSuccess, showToastError } from "../../utils";
import { calendarEventService, emailTemplateService } from "../../services";
import mapEmailBody from "../../pages/Task/Mapping";
import { useCalendarData } from "./useCalendarData";
import { useEmailTemplate } from "../useEmailTemplate";
import { formatDateForForm, formatTimeForForm, createUTCDate, getRoundedDate } from "../../utils/dateUtils";
import { IEvent } from "../../components/features/calendar/EventCalendar";

export const useEventUpdate = (
  eventToBeUpdated?: IEvent,
  identityIds?: string[],
) => {
  const [alertVariant, setAlertVariant] = useState("danger");
  const currentUser = useAtom(userAtom);
  const actioner = `${currentUser?.identity.displayName}: ${currentUser?.identity.email}`;
  
  const { allAppointmentTypes, allIdentities } = useCalendarData();
  const { emailTemplate, eventTemplate, createEmailArray } = useEmailTemplate("Event Reschedule");

  const handleSubmit = async (values: any, { setStatus, setSubmitting, setFieldError }: any) => {
    setStatus();
    setSubmitting(true);
    
    const calendarEntry = {
      id: eventToBeUpdated?.groupId,
      taskId: eventToBeUpdated?.taskId ?? undefined,
      title: values.title,
      description: values.description,
      appointmentTypeId: values.appointmentTypeId,
      startDate: createUTCDate(values.startDate, values.startTime),
      endDate: createUTCDate(values.endDate, values.endTime),
      isAllDay: values.isAllDay,
      identityIds: values.selectedIdentityIds,
      color: values.color,
    };

    try {
      const response = await calendarEventService.update(calendarEntry);
      setSubmitting(false);
      
      if (response.status !== "Failure") {
        // Send emails
        const personals = allIdentities?.filter(user => 
          calendarEntry.identityIds?.includes(user.value)
        ).map((item: any) => ({
          email: item?.label.split(": ")[1],
        }));
        
        const receiverEmails = await createEmailArray(eventTemplate, personals, currentUser?.identity.email ?? "");
        const eventId = eventToBeUpdated?.groupId ?? "";
        const updatedBody = await mapEmailBody({ 
          body: emailTemplate.body, 
          id: eventId, 
          actioner: actioner 
        });
        const updatedSubject = await mapEmailBody({ 
          body: emailTemplate.subject, 
          id: eventId, 
          actioner: actioner 
        });
        
        if (receiverEmails?.length > 0) {
          await emailTemplateService.sendAutoEmail(updatedSubject, updatedBody, receiverEmails);
        }
        showToastSuccess("Event updated successfully");
        return true;
      } else {
        setAlertVariant("danger");
        showToastError(response.message);
        return false;
      }
    } catch (error: any) {
      setAlertVariant("danger");
      if (error.status === 400 && error.errors) {
        Object.keys(error.errors).forEach((key) => {
          const messages = error.errors[key];
          if (Array.isArray(messages)) {
            messages.forEach((message) => showToastError(message));
          }
        });
      } else {
        showToastError("An unexpected error occurred!");
      }
      setSubmitting(false);
      return false;
    }
  };

  const getInitialValues = () => ({
    title: eventToBeUpdated?.title ?? "",
    description: eventToBeUpdated?.description ?? "",
    appointmentTypeId: eventToBeUpdated?.appointmentTypeId ?? "",
    selectedIdentityIds: identityIds as string[],
    startDate: formatDateForForm(eventToBeUpdated?.start, new Date()),
    startTime: formatTimeForForm(
      eventToBeUpdated?.start,
      getRoundedDate(30, new Date())
    ),
    endDate: formatDateForForm(eventToBeUpdated?.end, new Date()),
    endTime: formatTimeForForm(
      eventToBeUpdated?.end,
      getRoundedDate(30, new Date())
    ),
    notes: "",
    isAllDay: eventToBeUpdated?.allDay ?? false,
    color: eventToBeUpdated?.color,
  });

  return {
    alertVariant,
    currentUser,
    allAppointmentTypes,
    allIdentities,
    handleSubmit,
    getInitialValues,
  };
};
